import SocialSecurityCalculator from "../SocialSecurityApi";
import {getCurrentAgeYears, getMaritalStatus} from "../userDataTransformer";

export const SOCIAL_SECURITY_MIN_AGE = 62;
export const SOCIAL_SECURITY_MAX_AGE = 70;

class ScenarioDataBuilder {
    constructor(answers) {
        this.answers = answers;
        this.maritalStatus = getMaritalStatus(answers);
        this.mainSSCalc = new SocialSecurityCalculator('main', answers);
        this.mainAge = this.mainSSCalc.getCurrentAgeYears();
        if (this.maritalStatus === 'married') {
            this.spouseSSCalc = new SocialSecurityCalculator('spouse', answers);
            this.spouseAge = this.spouseSSCalc.getCurrentAgeYears();
        }
    }

    getSocialSecurityOptions(spouse) {
        if (this.maritalStatus === 'widowed') {
            return [Math.max(60, this.mainAge), Math.max(this.mainAge, 70)];
        } else if (this.maritalStatus === 'divorced' || this.maritalStatus === 'never-married') {
            return [Math.max(SOCIAL_SECURITY_MIN_AGE, this.mainAge), Math.max(this.mainAge, 70)];
        } else if (this.maritalStatus === 'married') {
            return [Math.max(SOCIAL_SECURITY_MIN_AGE, spouse ? this.spouseAge : this.mainAge), Math.max(this.mainAge, 70)];
        }
    }

    getAgeRange(spouse) {
        if (this.maritalStatus === 'widowed') {
            return [Math.max(60, this.mainAge), Math.max(this.mainAge, 70)];
        } else if (this.maritalStatus === 'divorced' || this.maritalStatus === 'never-married') {
            return [Math.max(SOCIAL_SECURITY_MIN_AGE, this.mainAge), Math.max(this.mainAge, 70)];
        } else if (this.maritalStatus === 'married') {
            let bottomAge = spouse ? this.spouseAge : this.mainAge;
            let maxAge = Math.max(this.spouseAge, this.mainAge);
            let adjustedBottomAge = bottomAge + Math.max(0, SOCIAL_SECURITY_MIN_AGE - maxAge);
            let difference = maxAge - (spouse ? this.mainAge : this.spouseAge);

            let endAge = SOCIAL_SECURITY_MAX_AGE + difference;
            return [adjustedBottomAge, endAge];
        }
    }

    getMainSocialSecurityAgeOptions() {
        let ageRange = this.getSocialSecurityOptions();
        let outputYears = [];
        for (let i = ageRange[0]; i <= ageRange[1]; i++) {
            outputYears.push(i);
        }
        return outputYears;
    }

    getMainYearsForScenario() {
        let ageRange = this.getAgeRange();
        let outputYears = [];
        for (let i = ageRange[0]; i <= ageRange[1]; i++) {
            outputYears.push(i);
        }
        return outputYears;
    }

    getSpouseYearsForScenario() {
        if (!this.spouseAge) {
            return []
        }

        let outputYears = [];
        if (this.maritalStatus === 'married') {
            let ageRange = this.getAgeRange(true);
            for (let i = ageRange[0]; i <= ageRange[1]; i++) {
                outputYears.push(i);
            }
        } else {
            for (let i = Math.max(62, this.spouseAge); i <= 70; i++) {
                outputYears.push(i);
            }
        }

        return outputYears;
    }

    getMarriedMainScenarioData(personalSelectionIndex, spousalSelectionIndex, otherSelectionIndex, otherSelectionType, spouse) {
        let displayAgeRange = this.getAgeRange(spouse);
        let age = spouse ? this.spouseAge : this.mainAge;
        let bottomAge = Math.max(SOCIAL_SECURITY_MIN_AGE, age);
        let topAge = Math.max(SOCIAL_SECURITY_MAX_AGE, age);
        let ageDifference = spouse ? this.spouseAge - this.mainAge : this.mainAge - this.spouseAge;

        let ssCalc = spouse ? this.spouseSSCalc : this.mainSSCalc;


        let output = [[{ type: 'header', text: 'Survivors Benefits' }], [{ type: 'header', text: 'Age Based Benefits' }]];
        for (let i = displayAgeRange[0]; i <= displayAgeRange[1]; i++) {
            let currentSpouseAge = i - ageDifference;
            let currentAge = i;
            let currentIndex = currentAge - displayAgeRange[0];

            if (this.maritalStatus === 'divorced') {
                currentSpouseAge = currentAge
            }

            if ((currentAge > topAge && (personalSelectionIndex === undefined && spousalSelectionIndex === undefined)) || currentAge < bottomAge) {
                output[0].push({ type: 'not-chosen' });
                output[1].push({ type: 'not-chosen' });
                continue;
            }

            if (spousalSelectionIndex !== undefined) {
                output[0].push({ type: 'not-chosen' });
            } else if (personalSelectionIndex !== undefined && currentIndex < personalSelectionIndex) {
                output[0].push({ type: 'not-chosen' });
            } else if (otherSelectionIndex !== undefined &&
                (currentIndex > otherSelectionIndex &&
                    (!(personalSelectionIndex === undefined || spousalSelectionIndex === undefined) || (otherSelectionType === 'spousal' && personalSelectionIndex === undefined)))) {
                output[0].push({ type: 'not-chosen' });
            } else {
                if (personalSelectionIndex !== undefined) {
                    output[0].push({ ...ssCalc.getBenefitForAge(displayAgeRange[0] + personalSelectionIndex), age: i });
                } else {
                    output[0].push({ ...ssCalc.getBenefitForAge(currentAge), age: currentAge });
                }
            }

            if (currentSpouseAge >= SOCIAL_SECURITY_MIN_AGE && otherSelectionType !== 'spousal') {
                if (personalSelectionIndex !== undefined) {
                    output[1].push({ type: 'disabled' })
                } else if (spousalSelectionIndex !== undefined && currentIndex < spousalSelectionIndex) {
                    output[1].push({ type: 'not-chosen' });
                } else if (otherSelectionIndex !== undefined &&
                    (currentIndex < otherSelectionIndex)) {
                    output[1].push({ type: 'not-chosen' });
                } else {
                    if (spousalSelectionIndex !== undefined) {
                        output[1].push({ ...ssCalc.getBenefitForAge(displayAgeRange[0] + spousalSelectionIndex, true), age: i });
                    } else {
                        output[1].push({ ...ssCalc.getBenefitForAge(i, true), age: i });
                    }
                }
            } else {
                output[1].push({ type: 'not-chosen' });
            }
        }

        return output;
    }

    getTotalHeaders() {
        let ageRange = this.getAgeRange();

        let outputYears = ["Monthly total benefit: "];
        for (let i = ageRange[0]; i <= ageRange[1]; i++) {
            outputYears.push(i);
        }
        return outputYears;
    }

    getMarriedSpouseScenarioData(personalSelectionIndex, spousalSelectionIndex, otherSelection, otherSelectionType) {
        return this.getMarriedMainScenarioData(personalSelectionIndex, spousalSelectionIndex, otherSelection, otherSelectionType, true);
    }

    getSingleScenarioData(choiceIndex) {
        let bottomAge = Math.max(SOCIAL_SECURITY_MIN_AGE, this.mainAge);
        let topAge = Math.max(SOCIAL_SECURITY_MAX_AGE, this.mainAge);

        let output = [{ type: 'header', text: 'Age Based Benefits' }];
        for (let i = bottomAge; i <= topAge; i++) {
            if (choiceIndex && i - bottomAge < choiceIndex) {
                output.push({ type: 'not-chosen' });
            } else {
                if (choiceIndex) {
                    output.push({ ...this.mainSSCalc.getBenefitForAge(bottomAge + choiceIndex, false), age: i });
                } else {
                    output.push({ ... this.mainSSCalc.getBenefitForAge(i, false), age: i });
                }
            }
        }

        return output;
    }

    getWidowedScenarioData(selectPersonal, selectSurvivors) {
        let output = [[{ type: 'header', text: 'Survivors Benefits' }], [{ type: 'header', text: 'Age Based Benefits' }]];

        let displayAgeRange = this.getAgeRange();
        for (let currentAge = displayAgeRange[0]; currentAge <= displayAgeRange[1]; currentAge++) {
            let currentIndex = currentAge - displayAgeRange[0];
            if ((selectSurvivors !== undefined && currentAge - displayAgeRange[0] < selectSurvivors) ||
                (currentIndex >= selectPersonal && selectSurvivors < selectPersonal) || (selectPersonal !== undefined && selectSurvivors === undefined && currentIndex === selectPersonal)) {
                output[0].push({ type: 'disabled' })
            } else {
                let amount = selectPersonal !== undefined ? this.mainSSCalc.getSurvivorsBenefits(displayAgeRange[0] + selectPersonal) : this.mainSSCalc.getSurvivorsBenefits(currentAge);
                output[0].push({ amount, age: currentAge });
            }

            if ((currentAge - displayAgeRange[0] < selectPersonal && selectPersonal !== undefined) ||
                (currentAge - displayAgeRange[0] >= selectSurvivors && selectPersonal < selectSurvivors) || (selectSurvivors !== undefined && selectPersonal === undefined && currentIndex === selectSurvivors)) {
                output[1].push({ type: 'disabled' })
            } else {
                let benefit = selectSurvivors !== undefined ? this.mainSSCalc.getBenefitForAge(displayAgeRange[0] + selectSurvivors, false) : this.mainSSCalc.getBenefitForAge(currentAge, false);
                output[1].push({ ...benefit, age: currentAge });
            }

        }

        return output
    }

    getMainHeaders() {
        let rawYears = this.getMainYearsForScenario();
        let FRA = this.mainSSCalc.getFRAAge(this.mainSSCalc.getYearBorn())
        rawYears[FRA.years - rawYears[0]] = 'FRA';
        return [this.answers.mainUserName + "'s Age: ", ...rawYears];
    }

    getSpouseHeaders() {
        let rawYears = this.getSpouseYearsForScenario();
        let FRA = this.spouseSSCalc.getFRAAge(this.spouseSSCalc.getYearBorn())
        rawYears[FRA.years - rawYears[0]] = 'FRA';
        return [this.answers.spouseUserName + "'s Age: ", ...rawYears];
    }

    buildMainProjections(allYears) {
        if (allYears) {
            return this.__buildProjectionsAllYears(this.mainSSCalc)
        }
        return this.__buildProjections(this.mainSSCalc);
    }

    buildSpouseProjections() {
        return this.__buildProjections(this.spouseSSCalc);
    }

    __buildProjectionsAllYears(calculator) {
        let outputObject = {};
        let years = this.getMainSocialSecurityAgeOptions();
        // let years = [64, 67, 70]
        let age = calculator.getCurrentAgeYears();
        years.forEach((age, index) => {
            let tempArr = new Array(index).fill({amount: 0});
            outputObject[age] = [...tempArr, ...calculator.getCumTaking(calculator.getYearBorn() + age, calculator.getBenefitForAge(age, false).amountYearly)]
        });

        let transformedOutputData = [];
        for (let i = 0; i < 40; i++) {
            let currentElem = {age: age + i, year: calculator.getYearBorn() + age + i};
            years.forEach((age, index) => {
                currentElem[age] = outputObject[age][i].amount
            })
            if (currentElem.age < 75 || currentElem.age > 85) {
                continue
            }
            transformedOutputData.push(currentElem)
        }

        return transformedOutputData
    }

    __buildProjections(ssCalculator) {
        let outputObject = {};

        let fra = ssCalculator.getFRAAge(ssCalculator.getYearBorn());
        let currentAge = ssCalculator.getCurrentAgeYears();
        if (currentAge <= 62) {
            outputObject['youngOption'] = { age: 62 }
            outputObject['mediumOption'] = { age: fra.years }
            outputObject['oldOption'] = { age: 70 }
        } else if (currentAge > 62 && currentAge < fra.years) { //Check against months if Myles decides he wants it displayed with months
            outputObject['youngOption'] = { age: currentAge }
            outputObject['mediumOption'] = { age: fra.years }
            outputObject['oldOption'] = { age: 70 }
        } else if (currentAge >= fra.years && currentAge < 70) {
            outputObject['mediumOption'] = { age: currentAge }
            outputObject['oldOption'] = { age: 70 }
        } else {
            outputObject['oldOption'] = { age: currentAge }
        }
        console.log(outputObject)

        let yearBorn = ssCalculator.getYearBorn();

        if (outputObject['youngOption']) {
            let age = outputObject['youngOption']['age'];
            outputObject['youngOption']['values'] = ssCalculator.getCumTaking(yearBorn + age, ssCalculator.getBenefitForAge(age, false).amountYearly, false);
        }
        if (outputObject['mediumOption']) {
            let age = outputObject['mediumOption']['age'];
            outputObject['mediumOption']['values'] = ssCalculator.getCumTaking(yearBorn + age, ssCalculator.getBenefitForAge(age, false).amountYearly, false);
        }
        if (outputObject['oldOption']) {
            let age = outputObject['oldOption']['age'];
            outputObject['oldOption']['values'] = ssCalculator.getCumTaking(yearBorn + age, ssCalculator.getBenefitForAge(age, false).amountYearly, false);
        }

        const makeNumber = (age) => {
            let numberData = {
                age: age,
            }

            Object.keys(outputObject).forEach(optionKey => {
                numberData[optionKey] = outputObject[optionKey]['values'][age - outputObject[optionKey]['age']].amount
            })
            return numberData;
        }

        let bottomAge = Math.max(70, Math.floor(currentAge / 5) * 5 + (currentAge % 5 == 0 ? 0 : 5));

        let outputArray = [];

        for (let i = bottomAge; i <= 100; i += 5) {
            outputArray.push(makeNumber(i));
        }

        return {
            raw: outputObject,
            graphData: outputArray,
        };
    }

}

export default ScenarioDataBuilder;